import {
  AggregationSchema,
  ComparisonSchema,
  DimensionRoleSchema,
  FieldKeySchema,
  FilterOperatorSchema,
  FilterTargetSchema,
  GroupingSchema,
  IdSchema,
  LogicalDataTypeSchema,
  ScalarValueSchema,
  SelectionSchema,
  SortSchema,
  TimeGrainSchema,
  VisualizationTypeSchema,
} from '@aibi/contracts';
import { z } from 'zod';
import { AI_CONTEXT_VERSION } from './ai-context.js';

/**
 * Topes absolutos del contexto recibido por la red. Son mas holgados que
 * `DEFAULT_AI_CONTEXT_LIMITS` (que acota lo que construye el AI Core) y existen para
 * rechazar contextos fabricados fuera de `buildAIContext`.
 */
const MAX_TEXT = 1_000;
const MAX_LABEL = 200;
const MAX_ITEMS = 500;
const MAX_VALUES = 100;

const Text = (max: number) => z.string().min(1).max(max);
const LocaleSchema = z
  .string()
  .min(2)
  .max(35)
  .regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Locale BCP 47 invalido');

const FieldSchema = z.strictObject({
  id: IdSchema,
  kind: z.enum(['dimension', 'measure']),
  name: Text(MAX_LABEL),
  label: Text(MAX_LABEL),
  description: Text(MAX_TEXT).optional(),
  synonyms: z.array(Text(MAX_LABEL)).max(50),
  dataType: LogicalDataTypeSchema,
  role: DimensionRoleSchema.optional(),
  temporal: z.boolean(),
  geographic: z.boolean(),
  defaultTimeGrain: TimeGrainSchema.optional(),
  calculated: z.boolean(),
  hierarchyId: IdSchema.optional(),
  aggregation: AggregationSchema.optional(),
});

const VisualizationCapabilitySchema = z.strictObject({
  type: VisualizationTypeSchema,
  label: Text(MAX_LABEL),
  minDimensions: z.number().int().min(0).max(100),
  maxDimensions: z.number().int().min(0).max(100).nullable(),
  minMeasures: z.number().int().min(0).max(100),
  maxMeasures: z.number().int().min(0).max(100).nullable(),
  requiresTemporal: z.boolean(),
  requiresGeographic: z.boolean(),
});

const AnalysisSchema = z.strictObject({
  id: IdSchema,
  dimensions: z
    .array(
      z.strictObject({
        key: FieldKeySchema,
        dimensionId: IdSchema,
        timeGrain: TimeGrainSchema.optional(),
      }),
    )
    .max(MAX_VALUES),
  measures: z.array(z.strictObject({ key: FieldKeySchema, measureId: IdSchema })).max(MAX_VALUES),
  filters: z
    .array(
      z.strictObject({
        id: IdSchema,
        target: FilterTargetSchema,
        operator: FilterOperatorSchema,
        values: z.array(ScalarValueSchema).max(MAX_VALUES),
      }),
    )
    .max(MAX_VALUES),
  sorting: z.array(SortSchema).max(MAX_VALUES),
  limit: z.number().int().min(0).nullable(),
  grouping: GroupingSchema,
  visualization: VisualizationTypeSchema,
  selections: z.array(SelectionSchema).max(MAX_VALUES),
  drilldown: z.array(z.strictObject({ hierarchyId: IdSchema, levelId: IdSchema })).max(MAX_VALUES),
  comparison: ComparisonSchema.pick({ kind: true, output: true }).nullable(),
});

/**
 * Esquema estricto de `AIContext` para contextos que cruzan un limite de confianza (el
 * relay del servidor). Rechaza propiedades desconocidas: un cliente no puede colar SQL,
 * credenciales ni filas de datos en campos no declarados.
 */
export const AIContextSchema = z.strictObject({
  version: z.literal(AI_CONTEXT_VERSION),
  locale: LocaleSchema,
  model: z.strictObject({
    id: IdSchema,
    name: Text(MAX_LABEL),
    description: Text(MAX_TEXT).optional(),
    primaryDateDimensionId: IdSchema.optional(),
  }),
  fields: z.array(FieldSchema).max(MAX_ITEMS),
  hierarchies: z
    .array(
      z.strictObject({
        id: IdSchema,
        label: Text(MAX_LABEL),
        levels: z
          .array(z.strictObject({ id: IdSchema, label: Text(MAX_LABEL), dimensionId: IdSchema }))
          .max(MAX_VALUES),
      }),
    )
    .max(MAX_VALUES),
  businessRules: z
    .array(
      z.strictObject({
        id: IdSchema,
        name: Text(MAX_LABEL),
        description: Text(MAX_TEXT).optional(),
      }),
    )
    .max(MAX_VALUES),
  dictionary: z
    .array(
      z.strictObject({
        term: Text(MAX_LABEL),
        definition: Text(MAX_TEXT),
        relatedObjectIds: z.array(IdSchema).max(MAX_VALUES),
      }),
    )
    .max(MAX_ITEMS),
  visualizations: z.array(VisualizationCapabilitySchema).max(MAX_VALUES),
  analysis: AnalysisSchema.nullable(),
  members: z
    .array(
      z.strictObject({
        dimensionId: IdSchema,
        values: z
          .array(z.union([z.string().max(MAX_LABEL), z.number(), z.boolean()]))
          .max(MAX_VALUES),
      }),
    )
    .max(MAX_ITEMS),
  truncated: z.boolean(),
});
